// ─────────────────────────────────────────────────────────────
// Who receives a ticket's customer emails.
//
// The primary ("TO") recipient is resolved from three possible
// sources in a fixed order — a linked CRM contact wins, then a
// company-user contact, then the raw reporter fields an inbound
// email or API ingest left behind. The backend notifier walks
// the same order, so this mirrors what actually gets mailed.
// ─────────────────────────────────────────────────────────────

/** A resolved address, ready to display or copy. */
export interface Recipient {
  /** Display name, or null when we only ever had an address. */
  name: string | null;
  email: string;
}

/** The slice of a ticket the primary-recipient lookup reads. */
export interface RecipientSource {
  crmContact?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  contact?: { name?: string | null; email?: string | null } | null;
  reporterName?: string | null;
  reporterEmail?: string | null;
}

/** A CC row as the tickets API returns it. */
export interface CcSource {
  id?: string;
  name?: string | null;
  email?: string | null;
}

/** Trim, and treat blank/whitespace-only as absent. */
function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The address a customer notification is sent TO, or null when the
 * ticket has no reachable recipient (in which case no customer email
 * goes out at all).
 */
export function primaryRecipient(ticket: RecipientSource | null | undefined): Recipient | null {
  if (!ticket) return null;

  const crmEmail = clean(ticket.crmContact?.email);
  if (crmEmail) {
    const name = [clean(ticket.crmContact?.firstName), clean(ticket.crmContact?.lastName)]
      .filter(Boolean)
      .join(' ');
    return { name: clean(name), email: crmEmail };
  }

  const contactEmail = clean(ticket.contact?.email);
  if (contactEmail) {
    return { name: clean(ticket.contact?.name), email: contactEmail };
  }

  const reporterEmail = clean(ticket.reporterEmail);
  if (reporterEmail) {
    return { name: clean(ticket.reporterName), email: reporterEmail };
  }

  return null;
}

/** Normalize a CC row, dropping any that carry no address. */
export function ccRecipient(cc: CcSource | null | undefined): Recipient | null {
  const email = clean(cc?.email);
  return email ? { name: clean(cc?.name), email } : null;
}

/**
 * `Name <email>` for display, or the bare address when unnamed.
 * Never use this as the copy payload — copying should hand over the
 * address on its own so it can be pasted straight into a To: field.
 */
export function formatRecipient(recipient: Recipient): string {
  return recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;
}
